/**
 * Group encryption layer — MLS (Messaging Layer Security, RFC 9420).
 *
 * Wraps message payloads in forward-secret, post-compromise-secure group
 * ciphertext before they're placed inside a SignedEnvelope. The envelope
 * provides authenticity and ordering; this layer provides confidentiality
 * and group key management.
 *
 * Interface design — note the immutable-state convention. Every operation
 * that changes group state returns a new state object rather than mutating
 * in place. This matches MLS's own model (per RFC 9420 + ts-mls) and
 * prevents whole classes of bugs around concurrent group operations.
 *
 * Implementations:
 *   - `PlaintextGroupEncryption` — passthrough for tests and single-node dev.
 *   - `MlsGroupEncryption` — wraps ts-mls. Real RFC 9420 MLS.
 */

import type { Bytes } from '../identity';

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface GroupId {
  readonly value: string;
}

/**
 * Opaque, serializable group state. The shape depends on the implementation:
 *
 *   - PlaintextGroupEncryption: a minimal `{ memberCount }` object.
 *   - MlsGroupEncryption: the ts-mls ratchet tree + epoch secrets, serialized.
 *
 * Callers should treat this as opaque and store via StorageRouter. The state
 * contains secrets — it must be encrypted at rest.
 */
export interface GroupState {
  readonly impl: 'plaintext' | 'mls';
  readonly groupId: GroupId;
  readonly epoch: number;
  readonly memberCount: number;
  /** Implementation-specific serialized state. */
  readonly opaque: Bytes;
}

/**
 * A KeyPackage is what a member publishes when they want to be added to a
 * group. The committing member uses it to add them via a Proposal.
 *
 * For the plaintext impl, the key package is just a member id wrapped in
 * a Uint8Array. For MLS, it's the ts-mls KeyPackage bytes.
 */
export interface KeyPackage {
  readonly impl: 'plaintext' | 'mls';
  readonly memberLabel: string;
  readonly bytes: Bytes;
}

/**
 * Welcome message emitted when adding a member to a group. The committing
 * member sends this to the new member out-of-band (in our system: over a
 * group's OrbitDB stream after being signed inside an envelope).
 */
export interface WelcomeMessage {
  readonly impl: 'plaintext' | 'mls';
  readonly groupId: GroupId;
  readonly forMember: string;
  readonly bytes: Bytes;
}

/** Result of mutating an existing group state. */
export interface CommitResult {
  readonly newState: GroupState;
  readonly welcome?: WelcomeMessage;
}

/** Result of an encryption operation. */
export interface EncryptResult {
  readonly ciphertext: Bytes;
  readonly newState: GroupState;
  /** Local epoch counter, useful for ordering decisions outside MLS. */
  readonly generation: number;
}

/** Result of a decryption operation. */
export interface DecryptResult {
  readonly plaintext: Bytes;
  readonly newState: GroupState;
  readonly senderLabel: string;
  readonly generation: number;
}

/**
 * GroupEncryption — the interface every implementation satisfies.
 *
 * All state-changing operations return the new state. Callers must persist
 * the returned state (via StorageRouter) before discarding the prior state,
 * or they'll lose forward-secrecy material on the next message.
 */
export interface GroupEncryption {
  /** Generate a fresh key package this member can publish. */
  generateKeyPackage(memberLabel: string): Promise<{
    keyPackage: KeyPackage;
    privateBytes: Bytes;
  }>;

  /** Create a new group with this member as the only initial participant. */
  createGroup(opts: {
    groupId: GroupId;
    creatorLabel: string;
    creatorKeyPackage: KeyPackage;
    creatorPrivate: Bytes;
  }): Promise<GroupState>;

  /** Add a member by their key package. Returns new state + welcome message. */
  addMember(state: GroupState, newMember: KeyPackage): Promise<CommitResult>;

  /** Remove a member by label. Returns new state. */
  removeMember(state: GroupState, memberLabel: string): Promise<CommitResult>;

  /**
   * Join an existing group from a welcome message. The joining member must
   * have already generated a key package whose public part the existing
   * group received before sending the welcome.
   */
  joinGroup(opts: {
    welcome: WelcomeMessage;
    myKeyPackage: KeyPackage;
    myPrivate: Bytes;
  }): Promise<GroupState>;

  /** Encrypt a message for the group. Returns ciphertext + new state. */
  encrypt(state: GroupState, plaintext: Bytes): Promise<EncryptResult>;

  /** Decrypt a message received from another group member. */
  decrypt(state: GroupState, ciphertext: Bytes): Promise<DecryptResult>;

  /**
   * Process a non-application message (commit, proposal) and update state.
   * Returns new state and indicates what changed.
   */
  processControl(state: GroupState, message: Bytes): Promise<{
    newState: GroupState;
    kind: 'commit' | 'proposal' | 'unknown';
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// PLAINTEXT IMPL — for tests and single-node dev
// ─────────────────────────────────────────────────────────────────────────────
// Provides NO confidentiality. Exists so the rest of the system can be wired
// up against the GroupEncryption interface without loading the MLS dependency.
// Useful in:
//   - Unit tests (avoid the ts-mls startup cost)
//   - Local dev when ciphertext semantics aren't the focus
//   - Single-node demos
//
// Any code path that needs real confidentiality MUST use MlsGroupEncryption.

interface PlaintextOpaque {
  members: string[];
  generation: number;
}

function encodePlaintextState(state: PlaintextOpaque): Bytes {
  return new TextEncoder().encode(JSON.stringify(state));
}

function decodePlaintextState(bytes: Bytes): PlaintextOpaque {
  return JSON.parse(new TextDecoder().decode(bytes)) as PlaintextOpaque;
}

export class PlaintextGroupEncryption implements GroupEncryption {
  async generateKeyPackage(memberLabel: string): Promise<{
    keyPackage: KeyPackage;
    privateBytes: Bytes;
  }> {
    const keyPackage: KeyPackage = {
      impl: 'plaintext',
      memberLabel,
      bytes: new TextEncoder().encode(memberLabel),
    };
    return { keyPackage, privateBytes: new Uint8Array(0) };
  }

  async createGroup(opts: {
    groupId: GroupId;
    creatorLabel: string;
    creatorKeyPackage: KeyPackage;
    creatorPrivate: Bytes;
  }): Promise<GroupState> {
    const opaque: PlaintextOpaque = { members: [opts.creatorLabel], generation: 0 };
    return {
      impl: 'plaintext',
      groupId: opts.groupId,
      epoch: 0,
      memberCount: 1,
      opaque: encodePlaintextState(opaque),
    };
  }

  async addMember(state: GroupState, newMember: KeyPackage): Promise<CommitResult> {
    const opaque = decodePlaintextState(state.opaque);
    if (!opaque.members.includes(newMember.memberLabel)) {
      opaque.members.push(newMember.memberLabel);
    }
    const newState: GroupState = {
      ...state,
      epoch: state.epoch + 1,
      memberCount: opaque.members.length,
      opaque: encodePlaintextState(opaque),
    };
    const welcome: WelcomeMessage = {
      impl: 'plaintext',
      groupId: state.groupId,
      forMember: newMember.memberLabel,
      bytes: encodePlaintextState(opaque),
    };
    return { newState, welcome };
  }

  async removeMember(state: GroupState, memberLabel: string): Promise<CommitResult> {
    const opaque = decodePlaintextState(state.opaque);
    opaque.members = opaque.members.filter((m) => m !== memberLabel);
    const newState: GroupState = {
      ...state,
      epoch: state.epoch + 1,
      memberCount: opaque.members.length,
      opaque: encodePlaintextState(opaque),
    };
    return { newState };
  }

  async joinGroup(opts: {
    welcome: WelcomeMessage;
    myKeyPackage: KeyPackage;
    myPrivate: Bytes;
  }): Promise<GroupState> {
    const opaque = decodePlaintextState(opts.welcome.bytes);
    return {
      impl: 'plaintext',
      groupId: opts.welcome.groupId,
      epoch: 0, // plaintext doesn't track epoch across the welcome boundary
      memberCount: opaque.members.length,
      opaque: encodePlaintextState(opaque),
    };
  }

  async encrypt(state: GroupState, plaintext: Bytes): Promise<EncryptResult> {
    const opaque = decodePlaintextState(state.opaque);
    opaque.generation += 1;
    const newState: GroupState = {
      ...state,
      opaque: encodePlaintextState(opaque),
    };
    // Passthrough — no actual encryption.
    return { ciphertext: plaintext, newState, generation: opaque.generation };
  }

  async decrypt(state: GroupState, ciphertext: Bytes): Promise<DecryptResult> {
    const opaque = decodePlaintextState(state.opaque);
    return {
      plaintext: ciphertext,
      newState: state, // plaintext decrypt doesn't advance state
      senderLabel: opaque.members[0] ?? 'unknown',
      generation: opaque.generation,
    };
  }

  async processControl(state: GroupState, _message: Bytes): Promise<{
    newState: GroupState;
    kind: 'commit' | 'proposal' | 'unknown';
  }> {
    return { newState: state, kind: 'unknown' };
  }
}
